/*
 * Extract props from a DOM element's data-* attributes.
 *
 * Supports four attribute patterns (parity with react-habitat):
 *   data-props="{json}"        — full JSON object merged into props
 *   data-prop-<name>="<value>" — string prop; "true"/"false"/"null" coerced; JSON objects/arrays parsed
 *   data-n-prop-<name>="<num>" — numeric prop via parseFloat
 *   data-r-prop-<name>="<ref>" — resolves window[ref]
 *
 * Merge precedence (intentional improvement over react-habitat):
 *   data-props is the base, then data-prop-*, data-n-prop-*, data-r-prop-* merge on top.
 *   react-habitat's precedence depended on DOM attribute order (fragile); this is predictable.
 *
 * The `proxy` prop (reference to the host element) that react-habitat injected is NOT injected here.
 * Components that need element access should use React refs instead.
 */

const PROP = 'data-prop-'
const PROP_JSON = 'data-props'
const PROP_NUMBER = 'data-n-prop-'
const PROP_REF = 'data-r-prop-'

const kebabToCamel = name =>
  name.replace(/-([a-z])/g, (_, c) => c.toUpperCase())

const stripPrefix = (name, prefix) => kebabToCamel(name.slice(prefix.length))

const parseStringProp = value => {
  if (value === '') return ''

  const lower = value.toLowerCase()
  if (lower === 'true') return true
  if (lower === 'false') return false
  if (lower === 'null') return null

  // JSON objects and arrays
  const first = value[0]
  const last = value[value.length - 1]
  if (
    value.length >= 2 &&
    ((first === '{' && last === '}') || (first === '[' && last === ']'))
  ) {
    try {
      return JSON.parse(value)
    } catch (e) {
      return value
    }
  }

  return value
}

/*
 * Strip dangerous keys (`__proto__`, `constructor`, `prototype`) before
 * merging an attacker-influenced JSON object into `props`. Prevents per-object
 * prototype pollution where e.g. `data-props='{"__proto__":{"isAdmin":true}}'`
 * would otherwise leak `props.isAdmin === true` via the prototype chain.
 */
const safeAssign = (target, source) => {
  for (const key of Object.keys(source)) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue
    }
    target[key] = source[key]
  }
}

const getElementProps = el => {
  const props = {}

  if (!el || !el.attributes) return props

  // Pass 1: data-props (base layer)
  const jsonAttr = el.getAttribute(PROP_JSON)
  if (jsonAttr) {
    try {
      const parsed = JSON.parse(jsonAttr)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        safeAssign(props, parsed)
      }
    } catch (e) {
      // Silent — same as react-habitat behavior
    }
  }

  // Pass 2: individual attributes merge on top. Each is a distinct DOM
  // attribute (not a JSON key), so prototype pollution is not a concern here.
  // Note: PROP === 'data-prop-' (with trailing hyphen), so `name.indexOf(PROP)`
  // does not match `data-props` (no hyphen before the 's') — no guard needed.
  for (let i = 0; i < el.attributes.length; i++) {
    const attr = el.attributes[i]
    const { name, value } = attr

    if (name.indexOf(PROP_NUMBER) === 0) {
      props[stripPrefix(name, PROP_NUMBER)] = parseFloat(value)
    } else if (name.indexOf(PROP_REF) === 0) {
      props[stripPrefix(name, PROP_REF)] =
        typeof window !== 'undefined' ? window[value] : undefined
    } else if (name.indexOf(PROP) === 0) {
      props[stripPrefix(name, PROP)] = parseStringProp(value)
    }
  }

  return props
}

export { getElementProps }
export default getElementProps
